import getFeaturedItems, {
  IFeaturedItem
} from '@anghami/neogateway/dist/endpoints/getFeaturedItems';
import { userContext } from 'components/contexts/auth/auth.context';
import DropDown from 'components/Reusable/DropDown';
import { gateway } from 'helpers/gateway-helper';
import { IOption } from 'interfaces/formsComponents.interface';
import React from 'react';
import { useQuery } from 'react-query';
import SearchIcon from 'icons/search.svg';
import { FeaturedTypes } from '../Interfaces/featured.interface';
import { useTranslation } from 'react-i18next';
import styles from './searchFeaturedInput.module.scss';
import { parseAnghamiUrl } from 'helpers/url-parser-helper';
import { getItemDetailsById } from 'helpers/http-helper';
import { toastContext } from 'components/contexts/toast/toast.context';

interface ISearchFeaturedInputProps {
  value?: IOption;
  onChange: (value: IFeaturedItem) => void;
  type: FeaturedTypes;
  selectedItemsIds: string[];
  disabled: boolean;
}
export default function SearchFeaturedInput({
  type,
  value,
  onChange,
  selectedItemsIds,
  disabled
}: ISearchFeaturedInputProps) {
  const { user } = React.useContext(userContext);
  const { t } = useTranslation();
  const { openToast } = React.useContext(toastContext);
  const [options, setOptions] = React.useState<IOption[]>([]);
  const [isProcessingPaste, setIsProcessingPaste] = React.useState(false);

  const { data } = useQuery(['user-featured-filled'], async () => {
    const { data } = await gateway.callEndpoint(getFeaturedItems, {
      operation: 'load',
      artistid: user?.anid
    });
    return data;
  });

  React.useEffect(() => {
    if (!data) return;
    setOptions(
      data[type]
        ?.filter((i) => selectedItemsIds.indexOf(i.id) === -1)
        .map((item) => ({ value: item.id, label: item.name }))
    );
  }, [data, selectedItemsIds]);

  const handleChange = (option: IOption) => {
    onChange(data[type].find((i) => i.id === option?.value));
  };

  const handlePaste = async (pastedText: string) => {
    if (isProcessingPaste || disabled) return;

    const parsedUrl = parseAnghamiUrl(pastedText);
    if (!parsedUrl) {
      openToast(t('Invalid Anghami URL'), 'error');
      return;
    }

    if (parsedUrl.featuredType !== type) {
      const expectedType = t(type);
      const pastedType = t(parsedUrl.featuredType);
      openToast(
        t('URL type mismatch. Expected %@ but got %@')
          .replace('%@', expectedType)
          .replace('%@', pastedType),
        'error'
      );
      return;
    }

    if (selectedItemsIds.includes(parsedUrl.id)) {
      openToast(t('Item is already added to featured items'), 'warning');
      return;
    }

    setIsProcessingPaste(true);
    try {
      const itemDetails = await getItemDetailsById(parsedUrl.id, parsedUrl.type);

      if (!itemDetails) {
        openToast(t('Could not fetch item details. Please check the URL and try again.'), 'error');
        return;
      }

      onChange(itemDetails);
      openToast(t('Item added successfully'), 'success');
    } catch (error) {
      console.error('Error processing pasted URL:', error);
      openToast(t('Error adding item. Please try again.'), 'error');
    } finally {
      setIsProcessingPaste(false);
    }
  };

  return (
    <DropDown
      value={value}
      onChange={handleChange}
      options={options}
      hideArrow={true}
      placeholder={t('featured search placeholder').replace('%@', t(type))}
      icon={<SearchIcon width={20} height={20} />}
      customInputClass={styles.customDropDownStyles}
      clearOnSelect
      disabled={disabled || isProcessingPaste}
      onPasteText={handlePaste}
    />
  );
}
